import React, { useState } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { Calendar, ExternalLink, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-156f UX — "Add to Calendar" button for booked talent.
 *
 * Renders a single trigger button. Opens a popover with two options:
 *   1. Google Calendar — opens the universal /calendar/render URL in a
 *      new tab. Pre-filled with role / venue / date / 12-hour time /
 *      event timezone / address / client / important notes.
 *   2. Apple / Outlook (.ics) — downloads an RFC-5545 calendar file
 *      via a Bearer-authenticated fetch (so we can authorise the
 *      booking) and triggers a browser download.
 *
 * Only renders for the booked talent — the API gate enforces that.
 *
 * Props:
 *   bookingId       (required)
 *   variant         "default" | "ghost" | "outline"   (default "outline")
 *   size            "sm" | "default" | "lg"           (default "sm")
 *   label           override button text              (default "Add to Calendar")
 *   className       extra classes
 */
export const AddToCalendarButton = ({
  bookingId,
  variant = 'outline',
  size = 'sm',
  label = 'Add to Calendar',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const openGoogle = async () => {
    if (!bookingId) return;
    try {
      setLoading(true);
      const res = await api.get(`/talent/bookings/${bookingId}/calendar/google`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
        setOpen(false);
      } else {
        toast.error('Could not generate calendar link');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate calendar link');
    } finally {
      setLoading(false);
    }
  };

  const downloadIcs = async () => {
    if (!bookingId) return;
    try {
      setLoading(true);
      // We need the auth token attached, so use api with responseType:'blob'
      const res = await api.get(`/talent/bookings/${bookingId}/calendar.ics`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename from Content-Disposition or fall back
      const disp = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(disp);
      a.download = m ? m[1] : `ievntur-shift-${bookingId}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          data-testid={`add-to-calendar-btn-${bookingId}`}
          aria-label="Add to Calendar"
        >
          <Calendar className="w-4 h-4 mr-1.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-2"
        data-testid={`add-to-calendar-popover-${bookingId}`}
      >
        <button
          type="button"
          onClick={openGoogle}
          disabled={loading}
          className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 flex items-center gap-2 text-sm disabled:opacity-50"
          data-testid={`add-to-calendar-google-${bookingId}`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4 text-cyan-600" />
          )}
          <span>Google Calendar</span>
        </button>
        <button
          type="button"
          onClick={downloadIcs}
          disabled={loading}
          className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 flex items-center gap-2 text-sm disabled:opacity-50"
          data-testid={`add-to-calendar-ics-${bookingId}`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4 text-cyan-600" />
          )}
          <span>Apple / Outlook (.ics)</span>
        </button>
        <p className="text-[11px] text-gray-500 px-3 pt-2">
          Times shown in event timezone, not your device's.
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default AddToCalendarButton;
